import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import type { GridPaginationModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "../../contexts/SnackbarContext";
import AiHubFilterBar from "./AiHubFilterBar";
import ConditionalTaggingResultTable from "./ConditionalTaggingResultTable";
import {
  fetchConditionalTaggingResultPage,
  type ConditionalTaggingResultRow,
} from "./conditionalTaggingResultData";
import { AI_HUB_DEFAULT_PAGE_SIZE } from "./constants";
import { canFetchTaggingResultsList, optionLabel } from "./taggingResultData";
import { resultLoadErrorMessage } from "./resultLoadError";
import { useAiHubObjectScope } from "./useAiHubObjectScope";

export default function ConditionalTaggingResultTab() {
  const { showMessage } = useSnackbar();
  const {
    objectType,
    objectSubType,
    conceptCode,
    conceptValue,
    objectId,
    setFilters,
    objectTypeOptions,
    planTypesLoading,
    getObjectSubTypeOptions,
  } = useAiHubObjectScope("conditionalTaggingResult");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: AI_HUB_DEFAULT_PAGE_SIZE,
  });
  const [rows, setRows] = useState<ConditionalTaggingResultRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const objectSubTypeOptions = getObjectSubTypeOptions(objectType);
  const canFetch = canFetchTaggingResultsList(
    objectType,
    objectSubType,
    objectSubTypeOptions.length,
  );

  const resetPage = useCallback(() => {
    setPaginationModel((current) =>
      current.page === 0 ? current : { ...current, page: 0 },
    );
  }, []);

  const handleObjectIdSearchChange = useCallback(
    (value: string) => {
      const next = value.trim();
      if (objectId === next) return;
      setFilters({ objectId: next });
      resetPage();
    },
    [objectId, resetPage, setFilters],
  );

  useEffect(() => {
    let mounted = true;

    if (planTypesLoading) {
      return () => {
        mounted = false;
      };
    }

    if (!canFetch) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      setHasLoaded(true);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);

    fetchConditionalTaggingResultPage({
      objectType,
      objectSubType,
      conceptCode,
      conceptValue,
      objectId,
      pageIndex: paginationModel.page,
      pageSize: paginationModel.pageSize,
    })
      .then((page) => {
        if (!mounted) return;
        setRows(page.rows);
        setTotal(page.total);
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setRows([]);
        setTotal(0);
        showMessage(
          resultLoadErrorMessage(
            err,
            "Failed to load Conditional Classification Results.",
          ),
          "error",
        );
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
        setHasLoaded(true);
      });

    return () => {
      mounted = false;
    };
  }, [
    canFetch,
    conceptCode,
    conceptValue,
    objectId,
    objectSubType,
    objectType,
    paginationModel.page,
    paginationModel.pageSize,
    planTypesLoading,
    showMessage,
  ]);

  const showInitialLoading = planTypesLoading || (!hasLoaded && loading);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        flex: 1,
        minHeight: 0,
        mx: 0,
      }}
    >
      <AiHubFilterBar
        objectType={objectType}
        objectSubType={objectSubType}
        conceptCode={conceptCode}
        conceptValue={conceptValue}
        dimensionSource="output"
        onObjectTypeChange={(nextType, nextSubType) => {
          setFilters({
            objectType: nextType,
            objectSubType: nextSubType,
            conceptCode: "",
            conceptValue: "",
          });
          resetPage();
        }}
        onObjectSubTypeChange={(nextSubType) => {
          setFilters({
            objectSubType: nextSubType,
            conceptCode: "",
            conceptValue: "",
          });
          resetPage();
        }}
        onConceptCodeChange={(nextConceptCode) => {
          setFilters({ conceptCode: nextConceptCode, conceptValue: "" });
          resetPage();
        }}
        onConceptValueChange={(nextValue) => {
          setFilters({ conceptValue: nextValue });
          resetPage();
        }}
      />
      {showInitialLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 320,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <ConditionalTaggingResultTable
          rows={rows}
          scopeObjectType={objectType}
          scopeObjectSubType={objectSubType}
          objectTypeLabel={optionLabel(objectTypeOptions, objectType)}
          objectSubTypeLabel={optionLabel(objectSubTypeOptions, objectSubType)}
          rowCount={total}
          selectionResetKey={`${objectType}|${objectSubType}|${conceptCode}|${conceptValue}|${objectId}`}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          objectIdSearch={objectId}
          onObjectIdSearchChange={handleObjectIdSearchChange}
        />
      )}
    </Box>
  );
}
